import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  formatFaxNumberInFaxDocument,
  REFERRAL_DRAFT_STORE,
  documentTitleFromMarkdown,
  documentDraftKeys,
  removeDocumentDraft,
  type DocumentDraft,
  useDocumentPdfPreview,
  withDocumentHeaderFields,
} from '@/entities/document-draft';
import {
  parseReferralRecipient,
  recipientName,
  recipientOrganization,
  REFERRAL_CHANNEL_LABELS,
  sendReferral,
  type ReferralChannel,
  type ReferralRecipient,
} from '@/entities/referral-recipient';
import {
  DocumentDraftEditor,
  useDocumentAttachments,
  useDocumentDraft,
} from '@/features/document-draft-editing';
import { RecipientSearchPopover } from '@/features/referral-recipient-search';
import { reportError } from '@/shared';
import { generateClientId, isLotusUuidV7 } from '@/shared/lib';
import { closeReferralEditor } from '@/shared/stores';
import { FaxNumberFormattingPlugin } from '@/shared/ui/primitives/lexical-markdown-editor';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

import { ReferralMessagePreview } from './ReferralMessagePreview';
import { ReferralSendDialog } from './ReferralSendDialog';

const PREVIEW_CHANNELS: ReadonlyArray<ReferralChannel> = ['nppes', 'metriport'];

interface ReferralEditorWorkspaceProps {
  patientId: string;
  draftId?: string;
  onClose: () => void;
}

export function ReferralEditorWorkspace({
  patientId,
  draftId,
  onClose,
}: ReferralEditorWorkspaceProps) {
  const queryClient = useQueryClient();
  const [showPreview, setShowPreview] = useState(true);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const sendAbortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const editor = useDocumentDraft({ patientId, draftId, store: REFERRAL_DRAFT_STORE });
  const recipient = parseReferralRecipient(editor.draft?.content.recipient);
  // Previews follow the recipient's channel; without one, either can be previewed.
  const [previewChannel, setPreviewChannel] = useState<ReferralChannel | null>(null);
  const channel = previewChannel ?? recipient?.channel ?? 'nppes';
  const attachments = useDocumentAttachments(patientId, editor.draft, editor.updateDraft);
  const preview = useDocumentPdfPreview(patientId, editor.draft, {
    mode:
      (showPreview || confirmationOpen) &&
      recipient?.channel === 'nppes' &&
      editor.loadState === 'ready'
        ? 'visible'
        : 'hidden',
    refresh: draftId !== undefined ? 'always' : 'cached',
  });
  const close = useCallback(() => {
    closeReferralEditor();
    onClose();
  }, [onClose]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sendAbortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    return () => {
      sendAbortControllerRef.current?.abort();
    };
  }, [draftId, patientId]);

  const setRecipient = (next: ReferralRecipient | null) => {
    if (editor.draft === null) {
      return;
    }
    editor.updateDraft({
      recipient: next ?? undefined,
      bodyMarkdown: withDocumentHeaderFields(editor.draft.content.bodyMarkdown, {
        To: next ? recipientName(next) : '',
        Organization: next ? recipientOrganization(next) : '',
        Fax: next?.channel === 'nppes' ? next.destination.practice_location.fax_number : '',
      }),
    });
    setPreviewChannel(null);
  };

  const canSend =
    editor.draft !== null &&
    recipient !== null &&
    documentTitleFromMarkdown(editor.draft.content.bodyMarkdown).trim().length > 0 &&
    editor.draft.content.bodyMarkdown.trim().length > 0 &&
    (editor.saveState === 'saved' || (!editor.isNewDraft && editor.saveState === 'idle')) &&
    !attachments.uploading;

  const handleSend = async () => {
    if (!canSend || editor.draft === null || recipient === null) {
      return;
    }
    const draft = editor.draft;
    const sendId = isLotusUuidV7(draft.id) ? draft.id : generateClientId();
    const draftToSend = sendId === draft.id ? draft : { ...draft, id: sendId };
    const abortController = new AbortController();
    sendAbortControllerRef.current?.abort();
    sendAbortControllerRef.current = abortController;
    setSending(true);
    setSendError(null);
    try {
      await sendReferral(patientId, draftToSend, recipient, abortController.signal);
      if (!mountedRef.current || abortController.signal.aborted) {
        return;
      }
      toast.success('Referral queued for delivery.', {
        description: `Sent to ${recipientName(recipient)}.`,
      });

      try {
        await removeDocumentDraft(patientId, draft.id, REFERRAL_DRAFT_STORE);
        if (!mountedRef.current || abortController.signal.aborted) {
          return;
        }
        queryClient.setQueryData(
          documentDraftKeys.patient(patientId, REFERRAL_DRAFT_STORE),
          (drafts: DocumentDraft[] = []) => drafts.filter((candidate) => candidate.id !== draft.id)
        );
      } catch (cleanupError) {
        reportError(cleanupError, {
          surface: 'handled',
          feature: 'referral-editor',
          operation: 'removeSentReferralDraft',
          extra: { patientId, draftId: draft.id },
        });
        if (mountedRef.current && !abortController.signal.aborted) {
          toast.info('Referral sent, but the draft could not be removed.', {
            description: 'It is safe to close this editor and remove the draft later.',
          });
        }
      }

      try {
        await queryClient.invalidateQueries({ queryKey: ['patient-referrals', patientId] });
        await queryClient.invalidateQueries({
          queryKey: ['patient-outbound-referrals', patientId],
        });
      } catch (refreshError) {
        reportError(refreshError, {
          surface: 'handled',
          feature: 'referral-editor',
          operation: 'refreshSentReferrals',
          extra: { patientId, draftId: draft.id },
        });
      }
      if (!mountedRef.current || abortController.signal.aborted) {
        return;
      }
      setConfirmationOpen(false);
      close();
    } catch (error) {
      if (!mountedRef.current || abortController.signal.aborted) {
        return;
      }
      setSendError(error instanceof Error ? error.message : 'Referral could not be sent.');
    } finally {
      if (sendAbortControllerRef.current === abortController) {
        sendAbortControllerRef.current = null;
      }
      if (mountedRef.current) {
        setSending(false);
      }
    }
  };

  return (
    <DocumentDraftEditor
      documentLabel="referral"
      backLabel="Referrals"
      onBack={close}
      onSend={() => {
        setSendError(null);
        setConfirmationOpen(true);
      }}
      sendDisabled={!canSend || sending}
      editor={editor}
      placeholder="Write the referral…"
      editorPlugins={<FaxNumberFormattingPlugin />}
      formatMarkdown={formatFaxNumberInFaxDocument}
      attachments={attachments}
      previewUrl={preview.previewUrl}
      previewState={preview.previewState}
      showPreview={showPreview}
      onShowPreviewChange={setShowPreview}
      fields={
        <div className="flex shrink-0 items-center gap-3 border-b px-4 py-2 text-sm">
          <span className="text-xs text-muted-foreground">To</span>
          <RecipientSearchPopover
            patientId={patientId}
            recipient={recipient}
            onSelect={setRecipient}
            onClear={() => setRecipient(null)}
          />
        </div>
      }
      previewToolbar={
        <div className="shrink-0 border-b bg-background px-4 py-3">
          <Tabs
            value={channel}
            onValueChange={(value) => setPreviewChannel(value as ReferralChannel)}
          >
            <TabsList aria-label="Preview channel">
              {PREVIEW_CHANNELS.map((id) => (
                <TabsTrigger key={id} value={id}>
                  {REFERRAL_CHANNEL_LABELS[id]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      }
      previewContent={
        channel === 'metriport' && editor.draft !== null ? (
          <ReferralMessagePreview draft={editor.draft} />
        ) : undefined
      }
      renderDialogs={(draft) =>
        recipient ? (
          <ReferralSendDialog
            draft={draft}
            patientId={patientId}
            recipient={recipient}
            open={confirmationOpen}
            sending={sending}
            error={sendError}
            faxPreviewUrl={preview.previewUrl}
            faxPreviewState={preview.previewState}
            onOpenChange={setConfirmationOpen}
            onSend={() => void handleSend()}
          />
        ) : null
      }
    />
  );
}
